import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import useFetch from "../../hooks/fetchMSSQL-hook";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Title,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Title,
  Legend
);

const SERIES_KEYS = ["Y", "Z", "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P"];
const CHART_INTERVAL_HOURS = 3;
const DEFAULT_FORECAST_DAYS = 7;
const DEFAULT_SUFFICIENCY_LIMIT_PERCENT = 50;
const FIXED_CHART_LEGEND_HEIGHT = 39;
const FIXED_Y_AXIS_WIDTH = 85;
const FIXED_HISTOGRAM_Y_AXIS_WIDTH = 60;
const FIXED_X_AXIS_HEIGHT = 46;
const STOCK_AXIS_COLOR = "#212529";
const HISTOGRAM_AXIS_COLOR = STOCK_AXIS_COLOR;
const WEEKDAY_NAMES = [
  "NIEDZIELA",
  "PONIEDZIAŁEK",
  "WTOREK",
  "ŚRODA",
  "CZWARTEK",
  "PIĄTEK",
  "SOBOTA",
];

const getHistogramDayIndex = (date) => (date.getDay() + 6) % 7;

const parseSufficiencyData = (value) => {
  if (!value) return null;

  if (typeof value === "object") {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (err) {
    console.error("Nie udało się sparsować danych wystarczalności", err);
    return null;
  }
};

const getCurrentChartStartDateTime = (intervalHours = CHART_INTERVAL_HOURS) => {
  const date = new Date();
  const safeIntervalHours = Math.max(1, Number(intervalHours) || CHART_INTERVAL_HOURS);
  const cycleHour = Math.floor(date.getHours() / safeIntervalHours) * safeIntervalHours;
  const padValue = (value) => String(value).padStart(2, "0");

  date.setHours(cycleHour, 0, 0, 0);

  return `${date.getFullYear()}-${padValue(date.getMonth() + 1)}-${padValue(date.getDate())} ${padValue(date.getHours())}:00:00`;
};

const getSufficiencyLimitPercent = (data, machine) => {
  const rawLimitPercent =
    machine?.SufficiencyLimitPercent
    ?? data?.SufficiencyLimitPercent;
  const numericLimitPercent = Number(rawLimitPercent);

  return Number.isFinite(numericLimitPercent)
    ? numericLimitPercent
    : DEFAULT_SUFFICIENCY_LIMIT_PERCENT;
};

const getSufficiencyBadgeClass = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return "badge-secondary";
  }

  const text = String(value).trim();

  if (text.toLowerCase() === "brak danych") return "badge-secondary";
  if (text.startsWith(">")) return "badge-info";
  if (/^0\s*h$/i.test(text)) return "badge-danger";
  if (/^\d+\s*h$/i.test(text)) return Number.parseInt(text, 10) <= 24 ? "badge-warning" : "badge-success";
  if (/^1\s*(?:dzień|d)(?:\s*0\s*h)?$/i.test(text)) return "badge-warning";
  if (/(?:^|\s)(?:dni|dzień|d)(?:\s|$)/i.test(text)) return "badge-success";

  const numericValue = Number(text);
  if (Number.isFinite(numericValue)) {
    if (numericValue <= 0) return "badge-danger";
    if (numericValue <= 24) return "badge-warning";
    return "badge-success";
  }

  return "badge-secondary";
};

const isAlgorithmAvailable = (algorithm) => {
  return Number(algorithm?.SufficiencyAlgorithmId) === 1;
};

const parseUpdateDateTime = (value) => {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  const match = String(value || "").match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
  );

  if (!match) return null;

  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
    Number(match[6] || 0)
  );
};

const getPointElapsedHours = (pointDateTime, startDateTime, pointIndex, intervalHours = CHART_INTERVAL_HOURS) => {
  const safeIntervalHours = Math.max(1, Number(intervalHours) || CHART_INTERVAL_HOURS);
  const fallbackHours = pointIndex * safeIntervalHours;
  const startDate = parseUpdateDateTime(startDateTime);
  const pointDate = parseUpdateDateTime(pointDateTime);

  if (!startDate || !pointDate) return fallbackHours;

  const elapsedHours = (pointDate.getTime() - startDate.getTime()) / (60 * 60 * 1000);

  return Number.isFinite(elapsedHours) ? elapsedHours : fallbackHours;
};

const padChartValue = (value) => String(value).padStart(2, "0");

const formatChartDateTime = (updateDateTime, elapsedHours) => {
  const startDate = parseUpdateDateTime(updateDateTime);

  if (!startDate) return `${elapsedHours}h`;

  const date = new Date(startDate.getTime() + (Number(elapsedHours) * 60 * 60 * 1000));

  return `${padChartValue(date.getDate())}.${padChartValue(date.getMonth() + 1)}, ${padChartValue(date.getHours())}:${padChartValue(date.getMinutes())}`;
};

const formatSufficiencyUpdateDateTime = (value) => {
  const date = parseUpdateDateTime(value);
  if (!date) return "Brak danych";

  return `${padChartValue(date.getDate())}.${padChartValue(date.getMonth() + 1)}.${date.getFullYear()} ${padChartValue(date.getHours())}:${padChartValue(date.getMinutes())}`;
};

const formatHistogramBucketTitle = (bucketStartDateTime, intervalHours = CHART_INTERVAL_HOURS) => {
  const startDate = parseUpdateDateTime(bucketStartDateTime);

  if (!startDate) return "";

  const endDate = new Date(
    startDate.getTime() + ((Math.max(1, Number(intervalHours) || CHART_INTERVAL_HOURS)) * 60 * 60 * 1000)
  );

  const startDateText = `${padChartValue(startDate.getDate())}.${padChartValue(startDate.getMonth() + 1)}`;
  const startTimeText = `${padChartValue(startDate.getHours())}:${padChartValue(startDate.getMinutes())}`;
  const endTimeText = `${padChartValue(endDate.getHours())}:${padChartValue(endDate.getMinutes())}`;

  return `${startDateText}, ${startTimeText}-${endTimeText}`;
};

const formatChartAxisTick = (updateDateTime, elapsedHours) => {
  const startDate = parseUpdateDateTime(updateDateTime);

  if (!startDate) return `${elapsedHours}h`;

  const hours = Number(elapsedHours);
  const date = new Date(startDate.getTime() + (hours * 60 * 60 * 1000));

  return `${padChartValue(date.getHours())}:${padChartValue(date.getMinutes())}`;
};

const normalizeToChartIntervalStart = (value, intervalHours = CHART_INTERVAL_HOURS) => {
  const date = parseUpdateDateTime(value);

  if (!date) return null;

  const safeIntervalHours = Math.max(1, Number(intervalHours) || CHART_INTERVAL_HOURS);
  const intervalHour = Math.floor(date.getHours() / safeIntervalHours) * safeIntervalHours;
  date.setHours(intervalHour, 0, 0, 0);

  return date;
};

const getHistogramBarColor = (value, maxValue, isHovered = false) => {
  const numericValue = Number(value) || 0;

  if (numericValue <= 0) {
    return isHovered ? "rgba(13, 110, 253, 0.08)" : "rgba(13, 110, 253, 0.035)";
  }

  if (maxValue <= 0) {
    return isHovered ? "rgba(13, 110, 253, 0.30)" : "rgba(13, 110, 253, 0.18)";
  }

  const ratio = numericValue / maxValue;

  if (ratio >= 0.65) {
    return isHovered ? "rgba(13, 110, 253, 0.34)" : "rgba(13, 110, 253, 0.24)";
  }

  if (ratio >= 0.3) {
    return isHovered ? "rgba(13, 110, 253, 0.29)" : "rgba(13, 110, 253, 0.20)";
  }

  return isHovered ? "rgba(13, 110, 253, 0.24)" : "rgba(13, 110, 253, 0.16)";
};

const buildHistogramDataset = ({
  histogramValues,
  chartStartDateTime,
  histogramStartDateTime,
  maxHours,
  maxHistogramValue,
  hoveredDayIndex,
  intervalHours = CHART_INTERVAL_HOURS,
}) => {
  const safeIntervalHours = Math.max(1, Number(intervalHours) || CHART_INTERVAL_HOURS);
  const histogramCenterOffsetHours = safeIntervalHours / 2;
  const chartStartDate = parseUpdateDateTime(chartStartDateTime);
  const histogramStartDate =
    normalizeToChartIntervalStart(histogramStartDateTime, safeIntervalHours)
    || normalizeToChartIntervalStart(chartStartDateTime, safeIntervalHours);
  const histogramPoints = [];

  if (chartStartDate && histogramStartDate && Array.isArray(histogramValues)) {
    histogramValues.forEach((rawValue, histogramIndex) => {
      const bucketStartDate = new Date(
        histogramStartDate.getTime()
          + (histogramIndex * safeIntervalHours * 60 * 60 * 1000)
      );

      const bucketStartHours =
        (bucketStartDate.getTime() - chartStartDate.getTime()) / (60 * 60 * 1000);
      const bucketEndHours = bucketStartHours + safeIntervalHours;

      if (bucketEndHours <= 0 || bucketStartHours >= maxHours) return;

      const bucketCenterHours = bucketStartHours + histogramCenterOffsetHours;

      histogramPoints.push({
        x: bucketCenterHours,
        y: Number(rawValue) || 0,
        originalX: bucketStartDate,
        histogramIndex,
        bucketStartHours,
        bucketEndHours,
      });
    });
  }

  return {
    type: "bar",
    label: "Prognozowana sprzedaż",
    xAxisID: "x",
    yAxisID: "ySales",
    parsing: false,

    clip: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },

    order: 10,
    barThickness: "flex",
    maxBarThickness: 48,
    categoryPercentage: 1,
    barPercentage: 0.82,
    borderRadius: 2,
    borderSkipped: false,
    backgroundColor: (ctx) => {
      const raw = ctx?.raw || {};
      const pointDate = parseUpdateDateTime(raw.originalX);
      const isHovered = pointDate
        ? hoveredDayIndex === getHistogramDayIndex(pointDate)
        : false;

      return getHistogramBarColor(raw.y, maxHistogramValue, isHovered);
    },
    borderColor: (ctx) => {
      const raw = ctx?.raw || {};
      const pointDate = parseUpdateDateTime(raw.originalX);
      const isHovered = pointDate
        ? hoveredDayIndex === getHistogramDayIndex(pointDate)
        : false;

      return isHovered
        ? "rgba(13, 110, 253, 0.72)"
        : "rgba(13, 110, 253, 0.38)";
    },
    borderWidth: 1,
    pointRadius: 0,
    pointHoverRadius: 0,
    data: histogramPoints,
  };
};

const getVisibleSeries = (series) => {
  if (!series) return [];

  const visibleKeys = ["Z", "A", "B", "C", "D", "E"];

  return SERIES_KEYS
    .filter(
      (key) =>
        visibleKeys.includes(key)
        && series[`${key}Name`] !== null
        && series[`${key}Name`] !== undefined
    )
    .map((key) => ({
      key,
      name: series[`${key}Name`],
      color: series[`${key}Color`] || "#000000",
    }));
};

const getProductSeriesGroups = (series) => {
  if (!series) return [];

  const productKeys = Object.keys(series)
    .map((key) => {
      const match = key.match(/^P(\d+)Name$/);
      return match ? { key: `P${match[1]}`, index: Number(match[1]) } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.index - b.index);

  const groups = new Map();

  productKeys.forEach(({ key }) => {
    const rawName = series[`${key}Name`];
    const name = String(rawName || "").trim();

    if (!name) return;

    const existing = groups.get(name);

    if (existing) {
      existing.keys.push(key);
      if (!existing.color && series[`${key}Color`]) {
        existing.color = series[`${key}Color`];
      }
      return;
    }

    groups.set(name, {
      name,
      keys: [key],
      color: series[`${key}Color`] || "#6C757D",
    });
  });

  return Array.from(groups.values());
};

const extendSeriesToChartEnd = (points, maxHours, intervalHours = CHART_INTERVAL_HOURS) => {
  if (!Array.isArray(points) || points.length === 0) return points;

  const result = [...points];
  const lastPoint = result[result.length - 1];
  const lastX = Number(lastPoint?.x);

  if (!lastPoint || !Number.isFinite(lastX) || lastX >= maxHours) {
    return result;
  }

  const safeIntervalHours = Math.max(1, Number(intervalHours) || CHART_INTERVAL_HOURS);

  for (
    let nextX = lastX + safeIntervalHours;
    nextX <= maxHours;
    nextX += safeIntervalHours
  ) {
    result.push({
      ...lastPoint,
      x: nextX,
    });
  }

  return result;
};

const getMaxTotalValue = (points) => {
  if (!Array.isArray(points) || points.length === 0) return 0;

  return points.reduce((maxValue, point) => {
    const numericY = Number(point?.Y);
    return Number.isFinite(numericY) ? Math.max(maxValue, numericY) : maxValue;
  }, 0);
};

const getMachineCapacity = (machine) => {
  const stateAndCapacity = String(machine?.StateAndCapacity || "");
  const parts = stateAndCapacity.split("/");

  if (parts.length < 2) return 0;

  const capacity = Number(String(parts[1]).trim().replace(",", "."));
  return Number.isFinite(capacity) && capacity > 0 ? capacity : 0;
};

const formatYAxisTick = (value, capacity) => {
  const quantity = Number(value);

  if (!Number.isFinite(quantity)) return value;
  if (!Number.isFinite(capacity) || capacity <= 0) return `${quantity}`;

  const percent = Math.round((quantity / capacity) * 100);
  return `${quantity} (${percent}%)`;
};

const dayBlocksPlugin = {
  id: "dayBlocks",
  beforeDraw: (chart, args, pluginOptions) => {
    const { ctx, chartArea } = chart;
    const startDate = parseUpdateDateTime(pluginOptions?.updateDateTime);
    const maxHours = Number(pluginOptions?.maxHours);

    if (!chartArea || !startDate || !Number.isFinite(maxHours)) return;

    const xScale = chart.scales.x;

    if (!xScale) return;

    const startMs = startDate.getTime();
    const endMs = startMs + (maxHours * 60 * 60 * 1000);
    const firstMidnight = new Date(startDate);
    firstMidnight.setHours(24, 0, 0, 0);

    ctx.save();

    let dayStart = new Date(firstMidnight);

    while (dayStart.getTime() < endMs) {
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);

      const startHour = (dayStart.getTime() - startMs) / (60 * 60 * 1000);
      const endHour = Math.min(
        maxHours,
        (dayEnd.getTime() - startMs) / (60 * 60 * 1000)
      );

      const startX = xScale.getPixelForValue(startHour);
      const endX = xScale.getPixelForValue(endHour);
      const isWeekend = dayStart.getDay() === 0 || dayStart.getDay() === 6;

      if (isWeekend && endHour > startHour) {
        ctx.fillStyle = "rgba(220, 53, 69, 0.055)";
        ctx.fillRect(startX, chartArea.top, endX - startX, chartArea.bottom - chartArea.top);
      }

      ctx.beginPath();
      ctx.strokeStyle = "rgba(108, 117, 125, 0.18)";
      ctx.lineWidth = 1;
      ctx.moveTo(startX, chartArea.top);
      ctx.lineTo(startX, chartArea.bottom);
      ctx.stroke();

      dayStart = dayEnd;
    }

    const hoveredDayIndex = pluginOptions?.hoveredDayIndex;

    if (Number.isInteger(hoveredDayIndex)) {
      const chartEndDate = new Date(endMs);
      const hoverDayStart = new Date(startDate);
      hoverDayStart.setHours(0, 0, 0, 0);

      let currentDay = new Date(hoverDayStart);

      while (currentDay.getTime() < chartEndDate.getTime()) {
        const currentDayEnd = new Date(currentDay);
        currentDayEnd.setDate(currentDayEnd.getDate() + 1);

        if (getHistogramDayIndex(currentDay) === hoveredDayIndex) {
          const rawStartHour = (currentDay.getTime() - startMs) / (60 * 60 * 1000);
          const rawEndHour = (currentDayEnd.getTime() - startMs) / (60 * 60 * 1000);
          const startHour = Math.max(0, rawStartHour);
          const endHour = Math.min(maxHours, rawEndHour);

          if (endHour > startHour) {
            const startX = xScale.getPixelForValue(startHour);
            const endX = xScale.getPixelForValue(endHour);

            ctx.fillStyle = "rgba(13, 110, 253, 0.08)";
            ctx.fillRect(
              startX,
              chartArea.top,
              endX - startX,
              chartArea.bottom - chartArea.top
            );
          }
        }

        currentDay = currentDayEnd;
      }
    }

    ctx.restore();
  },
  afterDraw: (chart, args, pluginOptions) => {
    if (!pluginOptions?.showWeekdayLabels) return;

    const { ctx, chartArea } = chart;
    const startDate = parseUpdateDateTime(pluginOptions?.updateDateTime);
    const maxHours = Number(pluginOptions?.maxHours);

    if (!chartArea || !startDate || !Number.isFinite(maxHours)) return;

    const xScale = chart.scales.x;

    if (!xScale) return;

    const startMs = startDate.getTime();
    const firstMidnight = new Date(startDate);
    firstMidnight.setHours(24, 0, 0, 0);

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    let blockStart = new Date(firstMidnight);

    while (true) {
      const blockEnd = new Date(blockStart);
      blockEnd.setDate(blockEnd.getDate() + 1);

      const startHour = (blockStart.getTime() - startMs) / (60 * 60 * 1000);
      const endHour = (blockEnd.getTime() - startMs) / (60 * 60 * 1000);

      if (startHour >= maxHours) break;

      const startX = xScale.getPixelForValue(Math.max(0, startHour));
      const endX = xScale.getPixelForValue(Math.min(maxHours, endHour));
      const centerX = startX + ((endX - startX) / 2);
      const histogramDayIndex = getHistogramDayIndex(blockStart);
      const isHovered = pluginOptions?.hoveredDayIndex === histogramDayIndex;

      ctx.font = isHovered
        ? '700 11px "Noto Mono", monospace'
        : '600 11px "Noto Mono", monospace';
      ctx.fillStyle = isHovered
        ? "rgba(13, 110, 253, 1)"
        : "rgba(73, 80, 87, 0.85)";
      ctx.fillText(
        WEEKDAY_NAMES[blockStart.getDay()],
        centerX,
        chartArea.top + 8
      );

      ctx.font = isHovered
        ? '700 10px "Noto Mono", monospace'
        : '500 10px "Noto Mono", monospace';
      ctx.fillText(
        `${padChartValue(blockStart.getDate())}.${padChartValue(blockStart.getMonth() + 1)}`,
        centerX,
        chartArea.top + 22
      );

      blockStart = blockEnd;
    }

    ctx.restore();
  },
  afterEvent: (chart, args, pluginOptions) => {
    if (!pluginOptions?.syncHistogramHover) return;

    const event = args?.event;
    const chartArea = chart.chartArea;
    const xScale = chart.scales.x;
    const startDate = parseUpdateDateTime(pluginOptions?.updateDateTime);
    const maxHours = Number(pluginOptions?.maxHours);

    if (!event || !chartArea || !xScale || !startDate || !Number.isFinite(maxHours)) {
      return;
    }

    let nextHoveredDayIndex = null;

    const isInsideChart =
      event.type !== "mouseout"
      && event.x >= chartArea.left
      && event.x <= chartArea.right
      && event.y >= chartArea.top
      && event.y <= chartArea.bottom;

    if (isInsideChart) {
      const hours = Number(xScale.getValueForPixel(event.x));

      if (Number.isFinite(hours) && hours >= 0 && hours <= maxHours) {
        const hoveredDate = new Date(
          startDate.getTime() + (hours * 60 * 60 * 1000)
        );

        nextHoveredDayIndex = getHistogramDayIndex(hoveredDate);
      }
    }

    const dayChanged = chart.$sufficiencyHoveredDayIndex !== nextHoveredDayIndex;

    if (dayChanged) {
      chart.$sufficiencyHoveredDayIndex = nextHoveredDayIndex;

      if (typeof pluginOptions?.onHoverDayChange === "function") {
        pluginOptions.onHoverDayChange(nextHoveredDayIndex);
      }

      args.changed = true;
    }
  },
};

const legendTextOffsetPlugin = {
  id: "legendTextOffset",
  afterUpdate: (chart, args, pluginOptions) => {
    const legend = chart.legend;

    if (!legend || legend.$textOffsetPatched) return;

    const originalDraw = legend.draw.bind(legend);

    legend.draw = () => {
      const ctx = chart.ctx;
      const offsetY = Number(pluginOptions?.offsetY ?? 2);
      const originalFillText = ctx.fillText;

      ctx.fillText = function fillLegendTextLower(textValue, x, y, ...rest) {
        return originalFillText.call(this, textValue, x, y + offsetY, ...rest);
      };

      try {
        originalDraw();
      } finally {
        ctx.fillText = originalFillText;
      }
    };

    legend.$textOffsetPatched = true;
  },
};

const legendSpacingPlugin = {
  id: "legendSpacing",
  afterInit: (chart, args, pluginOptions) => {
    const legend = chart.legend;

    if (!legend || legend.$spacingPatched) return;

    const originalFit = legend.fit.bind(legend);

    legend.fit = () => {
      originalFit();

      legend.height = Number(
        pluginOptions?.height ?? FIXED_CHART_LEGEND_HEIGHT
      );
    };

    legend.$spacingPatched = true;
  },
};

const legendLabelFilter = (legendItem, chartData) => {
  const dataset = chartData.datasets?.[legendItem.datasetIndex];
  return !dataset?.hideFromLegend;
};

const yAxisSuperscriptLabelsPlugin = {
  id: "yAxisSuperscriptLabels",
  afterDraw: (chart, args, pluginOptions) => {
    if (!pluginOptions?.enabled) return;

    const yScale = chart.scales?.[pluginOptions?.scaleId || "yStock"];
    const capacity = Number(pluginOptions?.capacity);

    if (!yScale || !Number.isFinite(capacity) || capacity <= 0) return;

    const { ctx } = chart;
    const baseFontSize = 12;
    const superscriptFontSize = 9;
    const gap = 2;
    const rightPadding = 8;

    ctx.save();

    yScale.ticks.forEach((tick) => {
      const quantity = Number(tick.value);

      if (!Number.isFinite(quantity)) return;

      const percent = Math.round((quantity / capacity) * 100);
      const percentText = `(${percent}%)`;
      const quantityText = `${quantity}`;
      const y = yScale.getPixelForValue(quantity);

      ctx.font = `${superscriptFontSize}px "Noto Mono", monospace`;
      const percentWidth = ctx.measureText(percentText).width;

      ctx.font = `${baseFontSize}px "Noto Mono", monospace`;
      const quantityWidth = ctx.measureText(quantityText).width;

      const totalWidth = percentWidth + gap + quantityWidth;
      const startX = yScale.right - rightPadding - totalWidth;
      const topY = y - (baseFontSize / 2);

      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillStyle = STOCK_AXIS_COLOR;

      ctx.font = `${superscriptFontSize}px "Noto Mono", monospace`;
      ctx.fillText(percentText, startX, topY);

      ctx.font = `${baseFontSize}px "Noto Mono", monospace`;
      ctx.fillText(quantityText, startX + percentWidth + gap, topY);
    });

    ctx.restore();
  },
};

export default function SufficiencyChartModal({
  machine,
  handleClose,
  onRecalculate,
  recalculating = false,
}) {
  const { fetchMssqlApi } = useFetch();
  const show = !!machine;
  const clientId = sessionStorage.getItem("clientId");
  const canViewProductChart = clientId === "dev" || clientId === "ret";
  const data = useMemo(
    () => parseSufficiencyData(machine?.Sufficiency),
    [machine?.Sufficiency]
  );
  const [activeTab, setActiveTab] = useState("chart");
  const [hoveredDayIndex, setHoveredDayIndex] = useState(null);
  const [showAlgorithmModal, setShowAlgorithmModal] = useState(false);
  const [chartRenderKey, setChartRenderKey] = useState(0);
  const [algorithms, setAlgorithms] = useState([]);
  const [algorithmsLoading, setAlgorithmsLoading] = useState(false);
  const [algorithmsLoaded, setAlgorithmsLoaded] = useState(false);
  const [selectedAlgorithmId, setSelectedAlgorithmId] = useState(null);
  const [sufficiencyPercentInput, setSufficiencyPercentInput] = useState("");
  const [updatingSufficiencyPercent, setUpdatingSufficiencyPercent] = useState(false);
  const [updatedSufficiencyLimitPercent, setUpdatedSufficiencyLimitPercent] = useState(null);

  useEffect(() => {
    if (!show) return;

    setActiveTab("chart");
    setHoveredDayIndex(null);
    setShowAlgorithmModal(false);
    setChartRenderKey(0);
    setAlgorithms([]);
    setAlgorithmsLoaded(false);
    setAlgorithmsLoading(false);
    setUpdatingSufficiencyPercent(false);
    setUpdatedSufficiencyLimitPercent(null);
    setSufficiencyPercentInput(String(getSufficiencyLimitPercent(data, machine)));

    const currentAlgorithmId = Number(
      data?.SufficiencyAlgorithmId ?? machine?.SufficiencyAlgorithmId
    );

    setSelectedAlgorithmId(
      Number.isFinite(currentAlgorithmId) && currentAlgorithmId > 0
        ? currentAlgorithmId
        : null
    );
  }, [
    show,
    machine?.MachineId,
    machine?.SufficiencyAlgorithmId,
    data?.SufficiencyAlgorithmId,
  ]);

  useEffect(() => {
    setHoveredDayIndex(null);
  }, [activeTab]);

  useEffect(() => {
    if (!show || updatedSufficiencyLimitPercent !== null) return;

    setSufficiencyPercentInput(String(getSufficiencyLimitPercent(data, machine)));
  }, [
    show,
    machine?.MachineId,
    machine?.SufficiencyLimitPercent,
    data?.SufficiencyLimitPercent,
    updatedSufficiencyLimitPercent,
  ]);

  useEffect(() => {
    if (!show || algorithmsLoaded || algorithmsLoading) return;

    setAlgorithmsLoading(true);

    fetchMssqlApi(
      "sufficiency-algorithms",
      {
        hideNotification: true,
      },
      (response) => {
        const nextAlgorithms = Array.isArray(response) ? response : [];

        setAlgorithms(nextAlgorithms);
        setSelectedAlgorithmId((previousId) => {
          if (Number.isFinite(Number(previousId)) && Number(previousId) > 0) {
            return Number(previousId);
          }

          const firstAvailableAlgorithm = nextAlgorithms.find(isAlgorithmAvailable);
          const fallbackAlgorithmId = Number(firstAvailableAlgorithm?.SufficiencyAlgorithmId);

          return Number.isFinite(fallbackAlgorithmId) ? fallbackAlgorithmId : null;
        });
        setAlgorithmsLoaded(true);
        setAlgorithmsLoading(false);
      },
      (err) => {
        console.error("Nie udało się pobrać algorytmów wystarczalności", err);
        setAlgorithms([]);
        setAlgorithmsLoaded(true);
        setAlgorithmsLoading(false);
      }
    );
  }, [
    show,
    algorithmsLoaded,
    algorithmsLoading,
    fetchMssqlApi,
  ]);

  const handleAlgorithmToggle = (algorithm) => {
    const algorithmId = Number(algorithm?.SufficiencyAlgorithmId);

    if (!isAlgorithmAvailable(algorithm)) {
      window.alert("Algorytm będzie dostępny wkrótce");
      return;
    }

    if (Number.isFinite(algorithmId)) {
      setSelectedAlgorithmId(algorithmId);
    }
  };

  const handleUpdateSufficiencyPercent = () => {
    if (updatingSufficiencyPercent) return;

    const machineId = Number(machine?.MachineId);
    const normalizedValue = String(sufficiencyPercentInput).replace(",", ".");
    const limitPercent = Number(normalizedValue);

    if (!Number.isSafeInteger(machineId) || machineId <= 0) {
      window.alert("Nieprawidłowy automat");
      return;
    }

    if (!Number.isFinite(limitPercent) || limitPercent < 0 || limitPercent > 100) {
      window.alert("Limit wystarczalności musi być w zakresie 0-100%");
      return;
    }

    const currentPercent = Number(
      updatedSufficiencyLimitPercent
      ?? getSufficiencyLimitPercent(data, machine)
    );

    if (Number.isFinite(currentPercent) && currentPercent === limitPercent) {
      return;
    }

    setUpdatingSufficiencyPercent(true);

    fetchMssqlApi(
      "sufficiency",
      {
        method: "PUT",
        hideNotification: true,
        data: {
          machineId,
          sufficiencyLimitPercent: limitPercent,
        },
      },
      (response) => {
        const savedPercent = Number(response?.SufficiencyLimitPercent);
        const nextPercent = Number.isFinite(savedPercent) ? savedPercent : limitPercent;

        setUpdatedSufficiencyLimitPercent(nextPercent);
        setSufficiencyPercentInput(String(nextPercent));
            setUpdatingSufficiencyPercent(false);
      },
      (err) => {
        console.error("Nie udało się zmienić limitu wystarczalności", err);
        setUpdatingSufficiencyPercent(false);
        window.alert("Nie udało się zmienić limitu wystarczalności");
      }
    );
  };

  const activeAlgorithm = useMemo(() => {
    const algorithmId = Number(
      selectedAlgorithmId
      ?? data?.SufficiencyAlgorithmId
      ?? machine?.SufficiencyAlgorithmId
    );

    if (!Number.isFinite(algorithmId)) return null;

    return algorithms.find(
      (algorithm) => Number(algorithm?.SufficiencyAlgorithmId) === algorithmId
    ) || null;
  }, [
    algorithms,
    selectedAlgorithmId,
    data?.SufficiencyAlgorithmId,
    machine?.SufficiencyAlgorithmId,
  ]);

  const algorithmStepHours = useMemo(() => {
    const value = Number(activeAlgorithm?.StepHours ?? data?.StepHours);
    return Number.isFinite(value) && value > 0 ? value : CHART_INTERVAL_HOURS;
  }, [activeAlgorithm?.StepHours, data?.StepHours]);

  const forecastDays = useMemo(() => {
    const value = Number(activeAlgorithm?.ForecastDays ?? data?.ForecastDays);
    return Number.isFinite(value) && value > 0 ? value : DEFAULT_FORECAST_DAYS;
  }, [activeAlgorithm?.ForecastDays, data?.ForecastDays]);

  const forecastHours = forecastDays * 24;
  const chart48hMaxHours = Math.min(48, forecastHours);

  const chartStartDateTime = useMemo(
    () => show
      ? normalizeToChartIntervalStart(machine?.SufficiencyUpdateDateTime, algorithmStepHours)
        || data?.Series?.Data?.[0]?.X
        || getCurrentChartStartDateTime(algorithmStepHours)
      : null,
    [show, machine?.MachineId, machine?.SufficiencyUpdateDateTime, data?.Series?.Data?.[0]?.X, algorithmStepHours]
  );

  const totalSufficiency = machine?.TotalSufficiency;
  const productSufficiency = machine?.ProductSufficiency;
  const sufficiencyLimitPercent = updatedSufficiencyLimitPercent
    ?? getSufficiencyLimitPercent(data, machine);
  const headerSufficiency = totalSufficiency !== null && totalSufficiency !== undefined && String(totalSufficiency).trim() !== ""
    ? `${totalSufficiency} > ${sufficiencyLimitPercent}%`
    : "Brak danych";

  const activeSeries = useMemo(
    () => getVisibleSeries(data?.Series),
    [data]
  );

  const activeSeries48h = useMemo(() => {
    const series = data?.Series48h || data?.Series;
    return getVisibleSeries(series);
  }, [data]);

  const productSeriesGroups = useMemo(
    () => getProductSeriesGroups(data?.Series),
    [data]
  );

  const histogramValues = useMemo(
    () => (Array.isArray(data?.Histogram) ? data.Histogram : []),
    [data]
  );

  const histogramMax = useMemo(() => {
    if (histogramValues.length === 0) return 0;
    return Math.max(...histogramValues.map((value) => Number(value) || 0));
  }, [histogramValues]);

  const machineCapacity = useMemo(
    () => getMachineCapacity(machine),
    [machine?.StateAndCapacity]
  );

  const chartData = useMemo(() => {
    const points = Array.isArray(data?.Series?.Data) ? data.Series.Data : [];

    const lineDatasets = activeSeries.map((series) => ({
      type: "line",
      label: series.name,
      xAxisID: "x",
      yAxisID: "yStock",
      data: extendSeriesToChartEnd(
        points
          .map((point, pointIndex) => ({
            x: getPointElapsedHours(point?.X, chartStartDateTime, pointIndex, algorithmStepHours),
            y: point[series.key] === null || point[series.key] === undefined
              ? null
              : Number(point[series.key]),
          }))
          .filter((point) => point.x >= 0 && point.x <= forecastHours),
        forecastHours,
        algorithmStepHours
      ),
      parsing: false,
      clip: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },

      order: 1,
      stack: series.key === "Y" ? "total" : "products",
      borderColor: series.color,
      backgroundColor: series.color,
      borderWidth: series.key === "Y" ? 4 : 2,
      pointRadius: (ctx) => ctx.raw?.y === 0 ? 4 : series.key === "Y" ? 2 : 1.5,
      pointHoverRadius: 6,
      pointHitRadius: 10,
      pointBackgroundColor: (ctx) => ctx.raw?.y === 0 ? "#DC3545" : series.color,
      pointBorderColor: (ctx) => ctx.raw?.y === 0 ? "#DC3545" : series.color,
      cubicInterpolationMode: "monotone",
      tension: 0.25,
      fill: false,
      spanGaps: false,
    }));

    return {
      datasets: [
        buildHistogramDataset({
          histogramValues,
          chartStartDateTime,
          histogramStartDateTime: data?.Series?.Data?.[0]?.X || chartStartDateTime,
          maxHours: forecastHours,
          maxHistogramValue: histogramMax,
          hoveredDayIndex,
          intervalHours: algorithmStepHours,
        }),
        ...lineDatasets,
      ],
    };
  }, [
    data,
    activeSeries,
    chartStartDateTime,
    histogramValues,
    histogramMax,
    hoveredDayIndex,
    forecastHours,
    algorithmStepHours,
  ]);

  const chartData48h = useMemo(() => {
    const series = data?.Series48h || data?.Series;
    const points = Array.isArray(series?.Data) ? series.Data : [];

    const lineDatasets = activeSeries48h.map((seriesConfig) => ({
      type: "line",
      label: seriesConfig.name,
      xAxisID: "x",
      yAxisID: "yStock",
      data: extendSeriesToChartEnd(
        points
          .map((point, pointIndex) => ({
            x: getPointElapsedHours(point?.X, chartStartDateTime, pointIndex, algorithmStepHours),
            y: point[seriesConfig.key] === null || point[seriesConfig.key] === undefined
              ? null
              : Number(point[seriesConfig.key]),
          }))
          .filter((point) => point.x >= 0 && point.x <= chart48hMaxHours),
        chart48hMaxHours,
        algorithmStepHours
      ),
      parsing: false,
      clip: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      order: 1,
      stack: seriesConfig.key === "Y" ? "total" : "products",
      borderColor: seriesConfig.color,
      backgroundColor: seriesConfig.color,
      borderWidth: seriesConfig.key === "Y" ? 4 : 2,
      pointRadius: (ctx) => ctx.raw?.y === 0 ? 4 : seriesConfig.key === "Y" ? 2 : 1.5,
      pointHoverRadius: 6,
      pointHitRadius: 10,
      pointBackgroundColor: (ctx) => ctx.raw?.y === 0 ? "#DC3545" : seriesConfig.color,
      pointBorderColor: (ctx) => ctx.raw?.y === 0 ? "#DC3545" : seriesConfig.color,
      cubicInterpolationMode: "monotone",
      tension: 0.25,
      fill: false,
      spanGaps: false,
    }));

    return {
      datasets: [
        buildHistogramDataset({
          histogramValues,
          chartStartDateTime,
          histogramStartDateTime: data?.Series?.Data?.[0]?.X || chartStartDateTime,
          maxHours: chart48hMaxHours,
          maxHistogramValue: histogramMax,
          hoveredDayIndex,
          intervalHours: algorithmStepHours,
        }),
        ...lineDatasets,
      ],
    };
  }, [
    data,
    activeSeries48h,
    chartStartDateTime,
    histogramValues,
    histogramMax,
    hoveredDayIndex,
    chart48hMaxHours,
    algorithmStepHours,
  ]);

  const productChartData = useMemo(() => {
    const points = Array.isArray(data?.Series?.Data) ? data.Series.Data : [];

    const datasets = productSeriesGroups.map((productGroup) => ({
      type: "line",
      label: productGroup.name,
      xAxisID: "x",
      yAxisID: "yStock",
      data: extendSeriesToChartEnd(
        points
          .map((point, pointIndex) => {
            let hasValue = false;
            let total = 0;

            productGroup.keys.forEach((key) => {
              const rawValue = point?.[key];

              if (rawValue === null || rawValue === undefined || rawValue === "") {
                return;
              }

              const numericValue = Number(rawValue);

              if (Number.isFinite(numericValue)) {
                hasValue = true;
                total += numericValue;
              }
            });

            return {
              x: getPointElapsedHours(point?.X, chartStartDateTime, pointIndex, algorithmStepHours),
              y: hasValue ? total : null,
            };
          })
          .filter((point) => point.x >= 0 && point.x <= forecastHours),
        forecastHours,
        algorithmStepHours
      ),
      parsing: false,
      clip: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      borderColor: productGroup.color,
      backgroundColor: productGroup.color,
      borderWidth: 2,
      pointRadius: (ctx) => ctx.raw?.y === 0 ? 4 : 1.5,
      pointHoverRadius: 6,
      pointHitRadius: 10,
      pointBackgroundColor: (ctx) => ctx.raw?.y === 0 ? "#DC3545" : productGroup.color,
      pointBorderColor: (ctx) => ctx.raw?.y === 0 ? "#DC3545" : productGroup.color,
      cubicInterpolationMode: "monotone",
      tension: 0.25,
      fill: false,
      spanGaps: false,
    }));

    return { datasets };
  }, [
    data,
    productSeriesGroups,
    chartStartDateTime,
    forecastHours,
    algorithmStepHours,
  ]);

  const productChartMaxY = useMemo(() => {
    return productChartData.datasets.reduce((maxValue, dataset) => {
      return dataset.data.reduce((datasetMax, point) => {
        const value = Number(point?.y);
        return Number.isFinite(value) ? Math.max(datasetMax, value) : datasetMax;
      }, maxValue);
    }, 0);
  }, [productChartData]);

  const chartPoints = Array.isArray(data?.Series?.Data) ? data.Series.Data : [];
  const chartPoints48h = Array.isArray((data?.Series48h || data?.Series)?.Data)
    ? (data?.Series48h || data?.Series).Data
    : [];

  const chartMaxY = useMemo(() => getMaxTotalValue(chartPoints), [data]);
  const chartMaxY48h = useMemo(() => getMaxTotalValue(chartPoints48h), [data]);

  const handleRecalculateChart = async () => {
    if (recalculating) return;

    try {
      if (typeof onRecalculate === "function") {
        await onRecalculate(machine);
      }
    } finally {
      setChartRenderKey((value) => value + 1);
      setHoveredDayIndex(null);
    }
  };

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: {
      autoPadding: false,
      padding: {
        top: 0,
        right: 8,
        bottom: 0,
        left: 0,
      },
    },
    interaction: {
      mode: "nearest",
      intersect: false,
    },
    plugins: {
      plnFormatter: false,
      dayBlocks: {
        updateDateTime: chartStartDateTime,
        maxHours: forecastHours,
        showWeekdayLabels: true,
        syncHistogramHover: true,
        hoveredDayIndex,
        onHoverDayChange: setHoveredDayIndex,
      },
      yAxisSuperscriptLabels: {
        enabled: true,
        scaleId: "yStock",
        capacity: machineCapacity,
      },
      legendSpacing: {
        height: FIXED_CHART_LEGEND_HEIGHT,
      },
      legendTextOffset: {
        offsetY: 1,
      },
      legend: {
        display: true,
        position: "top",
        labels: {
          boxWidth: 18,
          usePointStyle: false,
          filter: legendLabelFilter,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            const firstItem = items?.[0];

            if (firstItem?.dataset?.type === "bar" && firstItem?.raw?.originalX) {
              return formatHistogramBucketTitle(firstItem.raw.originalX, algorithmStepHours);
            }

            const hours = Number(firstItem?.parsed?.x);
            return Number.isFinite(hours)
              ? formatChartDateTime(chartStartDateTime, hours)
              : "";
          },
          label: (ctx) => {
            if (ctx.raw?.y === null || ctx.raw?.y === undefined) return "";

            const quantity = Number(ctx.raw.y) || 0;

            if (ctx.dataset?.type === "bar") {
              return `Prognozowana sprzedaż w ${algorithmStepHours}h: ${quantity} szt.`;
            }

            return quantity === 0
              ? `${ctx.dataset.label}: BRAK`
              : `${ctx.dataset.label}: ${quantity} szt.`;
          },
        },
      },
    },
    scales: {
      x: {
        type: "linear",
        min: 0,
        max: forecastHours,
        offset: false,
        bounds: "ticks",
        alignToPixels: true,
        afterFit: (scale) => {
          scale.height = FIXED_X_AXIS_HEIGHT;
        },
        title: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          stepSize: Math.max(algorithmStepHours, 12),
          maxRotation: 0,
          minRotation: 0,
          font: {
            family: '"Noto Mono", monospace',
          },
          callback: (value) => {
            const hours = Number(value);

            if (hours === forecastHours) return "";

            return formatChartAxisTick(chartStartDateTime, hours);
          },
        },
      },

      yStock: {
        type: "linear",
        position: "left",
        alignToPixels: true,
        stacked: true,
        beginAtZero: true,
        min: 0,
        afterFit: (scale) => {
          scale.width = FIXED_Y_AXIS_WIDTH;
        },
        max: machineCapacity > 0 ? machineCapacity : undefined,
        suggestedMax: machineCapacity > 0
          ? undefined
          : (chartMaxY > 0 ? Math.ceil(chartMaxY * 1.05) : undefined),
        title: {
          display: true,
          text: "Stan",
          color: STOCK_AXIS_COLOR,
          padding: 6,
            font: {
    weight: "bold",
  },
        },
        ticks: {
          precision: 0,
          includeBounds: true,
          color: "rgba(0, 0, 0, 0)",
          callback: (value) => `${value}`,
          padding: 3,
          font: {
            family: '"Noto Mono", monospace',
          },
        },
        grid: {
          color: "rgba(33, 37, 41, 0.10)",
          borderColor: STOCK_AXIS_COLOR,
        },
      },

      ySales: {
        type: "linear",
        position: "right",
        alignToPixels: true,
        stacked: false,
        beginAtZero: true,
        min: 0,
        afterFit: (scale) => {
          scale.width = FIXED_HISTOGRAM_Y_AXIS_WIDTH;
        },
        suggestedMax: histogramMax > 0 ? Math.ceil(histogramMax * 1.1) : 1,
        title: {
          display: true,
          text: "Sprzedaż",
          color: HISTOGRAM_AXIS_COLOR,
          padding: 6,
            font: {
    weight: "bold",
  },
        },
        ticks: {
          precision: 0,
          color: HISTOGRAM_AXIS_COLOR,
          callback: (value) => `${value}`,
          padding: 4,
          font: {
            family: '"Noto Mono", monospace',
          },
        },
        grid: {
          drawOnChartArea: false,
          color: "rgba(33, 37, 41, 0.28)",
          borderColor: HISTOGRAM_AXIS_COLOR,
        },
      },
    },
  }), [
    chartStartDateTime,
    hoveredDayIndex,
    chartMaxY,
    histogramMax,
    machineCapacity,
    forecastHours,
    algorithmStepHours,
  ]);

  const productChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: {
      autoPadding: false,
      padding: {
        top: 0,
        right: 8,
        bottom: 0,
        left: 0,
      },
    },
    interaction: {
      mode: "nearest",
      intersect: false,
    },
    plugins: {
      plnFormatter: false,
      dayBlocks: {
        updateDateTime: chartStartDateTime,
        maxHours: forecastHours,
        showWeekdayLabels: true,
        syncHistogramHover: false,
        hoveredDayIndex: null,
        onHoverDayChange: null,
      },
      legendSpacing: {
        height: FIXED_CHART_LEGEND_HEIGHT,
      },
      legendTextOffset: {
        offsetY: 1,
      },
      legend: {
        display: true,
        position: "top",
        labels: {
          boxWidth: 18,
          usePointStyle: false,
          filter: legendLabelFilter,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            const hours = Number(items?.[0]?.parsed?.x);
            return Number.isFinite(hours)
              ? formatChartDateTime(chartStartDateTime, hours)
              : "";
          },
          label: (ctx) => {
            if (ctx.raw?.y === null || ctx.raw?.y === undefined) return "";

            const quantity = Number(ctx.raw.y) || 0;

            return quantity === 0
              ? `${ctx.dataset.label}: BRAK`
              : `${ctx.dataset.label}: ${quantity} szt.`;
          },
        },
      },
    },
    scales: {
      x: {
        type: "linear",
        min: 0,
        max: forecastHours,
        offset: false,
        bounds: "ticks",
        alignToPixels: true,
        afterFit: (scale) => {
          scale.height = FIXED_X_AXIS_HEIGHT;
        },
        title: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          stepSize: Math.max(algorithmStepHours, 12),
          maxRotation: 0,
          minRotation: 0,
          font: {
            family: '"Noto Mono", monospace',
          },
          callback: (value) => {
            const hours = Number(value);

            if (hours === forecastHours) return "";

            return formatChartAxisTick(chartStartDateTime, hours);
          },
        },
      },
      yStock: {
        type: "linear",
        position: "left",
        alignToPixels: true,
        stacked: false,
        beginAtZero: true,
        min: 0,
        afterFit: (scale) => {
          scale.width = FIXED_Y_AXIS_WIDTH;
        },
        suggestedMax: productChartMaxY > 0
          ? Math.ceil(productChartMaxY * 1.05)
          : 1,
        title: {
          display: true,
          text: "Stan produktu",
          color: STOCK_AXIS_COLOR,
          padding: 6,
          font: {
            weight: "bold",
          },
        },
        ticks: {
          precision: 0,
          includeBounds: true,
          color: STOCK_AXIS_COLOR,
          callback: (value) => `${value}`,
          padding: 4,
          font: {
            family: '"Noto Mono", monospace',
          },
        },
        grid: {
          color: "rgba(33, 37, 41, 0.10)",
          borderColor: STOCK_AXIS_COLOR,
        },
      },
    },
  }), [
    chartStartDateTime,
    forecastHours,
    algorithmStepHours,
    productChartMaxY,
  ]);

  const chartOptions48h = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: {
      autoPadding: false,
      padding: {
        top: 0,
        right: 8,
        bottom: 0,
        left: 0,
      },
    },
    interaction: {
      mode: "nearest",
      intersect: false,
    },
    plugins: {
      plnFormatter: false,
      dayBlocks: {
        updateDateTime: chartStartDateTime,
        maxHours: chart48hMaxHours,
        showWeekdayLabels: false,
        syncHistogramHover: true,
        hoveredDayIndex,
        onHoverDayChange: setHoveredDayIndex,
      },
      yAxisSuperscriptLabels: {
        enabled: true,
        scaleId: "yStock",
        capacity: machineCapacity,
      },
      legendSpacing: {
        height: FIXED_CHART_LEGEND_HEIGHT,
      },
      legendTextOffset: {
        offsetY: 1,
      },
      legend: {
        display: true,
        position: "top",
        labels: {
          boxWidth: 18,
          usePointStyle: false,
          filter: legendLabelFilter,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            const firstItem = items?.[0];

            if (firstItem?.dataset?.type === "bar" && firstItem?.raw?.originalX) {
              return formatHistogramBucketTitle(firstItem.raw.originalX, algorithmStepHours);
            }

            const hours = Number(firstItem?.parsed?.x);
            return Number.isFinite(hours)
              ? formatChartDateTime(chartStartDateTime, hours)
              : "";
          },
          label: (ctx) => {
            if (ctx.raw?.y === null || ctx.raw?.y === undefined) return "";

            const quantity = Number(ctx.raw.y) || 0;

            if (ctx.dataset?.type === "bar") {
              return `Sprzedaż w ${algorithmStepHours}h: ${quantity} szt.`;
            }

            return quantity === 0
              ? `${ctx.dataset.label}: BRAK`
              : `${ctx.dataset.label}: ${quantity} szt.`;
          },
        },
      },
    },
    scales: {
      x: {
        type: "linear",
        min: 0,
        max: chart48hMaxHours,
        offset: false,
        bounds: "ticks",
        alignToPixels: true,
        afterFit: (scale) => {
          scale.height = FIXED_X_AXIS_HEIGHT;
        },
        title: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          stepSize: Math.max(algorithmStepHours, 6),
          maxRotation: 0,
          minRotation: 0,
          font: {
            family: '"Noto Mono", monospace',
          },
          callback: (value) => formatChartAxisTick(chartStartDateTime, Number(value)),
        },
      },
      yStock: {
        type: "linear",
        position: "left",
        alignToPixels: true,
        stacked: true,
        beginAtZero: true,
        min: 0,
        afterFit: (scale) => {
          scale.width = FIXED_Y_AXIS_WIDTH;
        },
        max: machineCapacity > 0 ? machineCapacity : undefined,
        suggestedMax: machineCapacity > 0
          ? undefined
          : (chartMaxY48h > 0 ? Math.ceil(chartMaxY48h * 1.05) : undefined),
        title: {
          display: true,
          text: "Stan",
          color: STOCK_AXIS_COLOR,
          padding: 6,
          font: {
            weight: "bold",
          },
        },
        ticks: {
          precision: 0,
          includeBounds: true,
          color: "rgba(0, 0, 0, 0)",
          callback: (value) => `${value}`,
          padding: 3,
          font: {
            family: '"Noto Mono", monospace',
          },
        },
        grid: {
          color: "rgba(33, 37, 41, 0.10)",
          borderColor: STOCK_AXIS_COLOR,
        },
      },
      ySales: {
        type: "linear",
        position: "right",
        alignToPixels: true,
        stacked: false,
        beginAtZero: true,
        min: 0,
        afterFit: (scale) => {
          scale.width = FIXED_HISTOGRAM_Y_AXIS_WIDTH;
        },
        suggestedMax: histogramMax > 0 ? Math.ceil(histogramMax * 1.1) : 1,
        title: {
          display: true,
          text: "Sprzedaż",
          color: HISTOGRAM_AXIS_COLOR,
          padding: 6,
          font: {
            weight: "bold",
          },
        },
        ticks: {
          precision: 0,
          color: HISTOGRAM_AXIS_COLOR,
          callback: (value) => `${value}`,
          padding: 4,
          font: {
            family: '"Noto Mono", monospace',
          },
        },
        grid: {
          drawOnChartArea: false,
          color: "rgba(33, 37, 41, 0.28)",
          borderColor: HISTOGRAM_AXIS_COLOR,
        },
      },
    },
  }), [
    chartStartDateTime,
    hoveredDayIndex,
    chartMaxY48h,
    histogramMax,
    machineCapacity,
    chart48hMaxHours,
    algorithmStepHours,
  ]);

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        centered
        dialogClassName="sufficiency-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center flex-wrap">
            <span className="mr-4">Wystarczalność</span>

            <span className="mr-4" style={{ fontSize: "12px", fontWeight: 500 }}>
              <b>Automat:</b> {machine?.MachineFullName || "-"}
            </span>

            <span className="mr-4" style={{ fontSize: "12px", fontWeight: 500 }}>
              <b>Wystarczalność:</b>{" "}
              <span className={`badge ${getSufficiencyBadgeClass(totalSufficiency)} sufficiency-text`}>{headerSufficiency}</span>
            </span>

            <span style={{ fontSize: "12px", fontWeight: 500 }}>
              <b>Wystarczalność (najlepszy produkt):</b>{" "}
              <span className={`badge ${getSufficiencyBadgeClass(productSufficiency)} sufficiency-text`}>
                {productSufficiency !== null && productSufficiency !== undefined && String(productSufficiency).trim() !== ""
                  ? productSufficiency
                  : "Brak danych"}
              </span>
            </span>

            <span className="ml-4" style={{ fontSize: "12px", fontWeight: 500 }}>
              <b>Przeliczono:</b> {formatSufficiencyUpdateDateTime(machine?.SufficiencyUpdateDateTime)}
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="pb-1 d-flex flex-column"
          style={{
            height: "calc(100vh - 150px)",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
        <div
          className="d-flex align-items-end mb-2 border-bottom"
          style={{ minHeight: "52px" }}
        >
          <ul
            className="nav nav-tabs mb-0 flex-shrink-0"
            style={{
              borderBottom: 0,
              marginBottom: "-1px",
            }}
          >
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === "chart" ? "active" : ""}`}
                onClick={() => setActiveTab("chart")}
              >
                Wykres {forecastDays} dni
              </button>
            </li>

            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === "chart48h" ? "active" : ""}`}
                onClick={() => setActiveTab("chart48h")}
              >
                Wykres 48h
              </button>
            </li>

            {canViewProductChart && (
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "products" ? "active" : ""}`}
                  onClick={() => setActiveTab("products")}
                >
                  Wykres produktów
                </button>
              </li>
            )}
          </ul>


          <div className="d-flex align-items-center ml-auto mb-2 pl-2">
            <div className="input-group input-group-sm mr-2" style={{ width: 105 }}>
              <input
                type="number"
                className="form-control"
                min="0"
                max="100"
                step="0.01"
                value={sufficiencyPercentInput}
                onChange={(event) => setSufficiencyPercentInput(event.target.value)}
                onBlur={handleUpdateSufficiencyPercent}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleUpdateSufficiencyPercent();
                  }
                }}
                disabled={updatingSufficiencyPercent}
                title="Limit wystarczalności"
              />
              <div className="input-group-append">
                <span className="input-group-text">%</span>
              </div>
            </div>

            <button
              type="button"
              className="btn d-flex align-items-center justify-content-center mr-2"
              style={{
                width: 40,
                height: 40,
                padding: 0,
                fontSize: 18,
                flexShrink: 0,
              }}
              onClick={handleRecalculateChart}
              disabled={recalculating}
            >
              <i
                className={`fas fa-sync-alt ${recalculating ? "fa-spin" : ""}`}
                aria-hidden="true"
              />
            </button>

            <button
              type="button"
              className="btn d-flex align-items-center justify-content-center"
              style={{
                width: 40,
                height: 40,
                padding: 0,
                fontSize: 20,
                flexShrink: 0,
              }}
              onClick={() => setShowAlgorithmModal(true)}
              title="Algorytmy"
            >
              <i className="fa-solid fa-microchip" aria-hidden="true" />
            </button>
          </div>
        </div>

        {activeTab === "chart" && (
          <div
            style={{
              width: "100%",
              height: "100%",
              flex: "1 1 auto",
              minHeight: 0,
              position: "relative",
            }}
          >
            {data ? (
              <Line
                key={`sufficiency-7d-${machine?.MachineId || "none"}-${chartRenderKey}`}
                data={chartData}
                options={chartOptions}
                plugins={[
                  dayBlocksPlugin,
                  legendSpacingPlugin,
                  legendTextOffsetPlugin,
                  yAxisSuperscriptLabelsPlugin,
                ]}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                Brak danych o wystarczalności.
              </div>
            )}
          </div>
        )}

        {activeTab === "chart48h" && (
          <div
            style={{
              width: "100%",
              height: "100%",
              flex: "1 1 auto",
              minHeight: 0,
              position: "relative",
            }}
          >
            {data ? (
              <Line
                key={`sufficiency-48h-${machine?.MachineId || "none"}-${chartRenderKey}`}
                data={chartData48h}
                options={chartOptions48h}
                plugins={[
                  dayBlocksPlugin,
                  legendSpacingPlugin,
                  legendTextOffsetPlugin,
                  yAxisSuperscriptLabelsPlugin,
                ]}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                Brak danych o wystarczalności.
              </div>
            )}
          </div>
        )}


        {canViewProductChart && activeTab === "products" && (
          <div
            style={{
              width: "100%",
              height: "100%",
              flex: "1 1 auto",
              minHeight: 0,
              position: "relative",
            }}
          >
            {productChartData.datasets.length > 0 ? (
              <Line
                key={`sufficiency-products-${machine?.MachineId || "none"}-${chartRenderKey}`}
                data={productChartData}
                options={productChartOptions}
                plugins={[
                  dayBlocksPlugin,
                  legendSpacingPlugin,
                  legendTextOffsetPlugin,
                ]}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                Brak danych per produkt.
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      </Modal>

      <Modal
        show={showAlgorithmModal}
        onHide={() => setShowAlgorithmModal(false)}
        backdrop="static"
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Algorytmy</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div
            className="border rounded d-flex flex-column overflow-hidden"
            style={{ minHeight: "320px", maxHeight: "520px" }}
          >

            <div className="flex-grow-1 overflow-auto custom-scroll">
              {algorithmsLoading ? (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted py-5">
                </div>
              ) : algorithms.length === 0 ? (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted py-5">
                  Brak algorytmów
                </div>
              ) : (
                algorithms.map((algorithm) => {
                  const algorithmId = Number(algorithm?.SufficiencyAlgorithmId);
                  const isAvailable = isAlgorithmAvailable(algorithm);
                  const isSelected = Number(selectedAlgorithmId) === algorithmId;

                  return (
                    <label
                      key={algorithm.SufficiencyAlgorithmId}
                      className={`d-flex align-items-start px-3 py-3 mb-0 border-bottom ${
                        isAvailable ? "" : "text-muted"
                      }`}
                      style={{
                        cursor: "pointer",
                        background: isSelected ? "rgba(13, 110, 253, 0.06)" : "#fff",
                      }}
                    >
                      <input
                        type="radio"
                        name="sufficiencyAlgorithm"
                        className="mt-1 mr-3"
                        checked={isSelected}
                        aria-disabled={!isAvailable}
                        onChange={() => handleAlgorithmToggle(algorithm)}
                      />

                      <div className="flex-grow-1 pr-3">
                        <div className="d-flex align-items-center flex-wrap">
                          <span className="font-weight-bold mr-2">
                            {algorithm.Name}
                          </span>

                          <span
                            className={`badge ${
                              isAvailable ? "badge-success" : "badge-secondary"
                            }`}
                          >
                            {isAvailable ? "Dostępny" : "Niedostępny"}
                          </span>
                        </div>

                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowAlgorithmModal(false)}
          >
            Zamknij
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}